import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Camera, Loader2, Upload, ScanLine, CheckCircle2, ArrowLeft, ArrowRight, X,
} from "lucide-react";
import { api } from "@/lib/api";
import IntelligenceHub from "./IntelligenceHub";

/**
 * OCR de carte de visite — Claude Sonnet 4.5 Vision via Emergent LLM Key.
 * L'image reste en mémoire côté backend (jamais persistée).
 */
export default function TargetOcrCard() {
  const navigate = useNavigate();
  const [step, setStep] = useState("capture"); // capture · review · saving
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState(null); // {url, name}
  const [extracted, setExtracted] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Format non supporté. Utilisez JPEG, PNG ou WEBP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image trop volumineuse (maximum 5 Mo).");
      return;
    }
    setPreview({ url: URL.createObjectURL(file), name: file.name });
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/cdl/targets/ocr-card", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setExtracted(data.extracted);
      setForm({
        target_type: "prospect",
        display_name: data.extracted.display_name || "",
        first_name: data.extracted.first_name || "",
        last_name: data.extracted.last_name || "",
        organization_name: data.extracted.organization_name || "",
        role_or_title: data.extracted.role_or_title || "",
        email: data.extracted.email || "",
        phone: data.extracted.phone || "",
        website: data.extracted.website || "",
        linkedin_url: data.extracted.linkedin_url || "",
        address: data.extracted.address || "",
        city: data.extracted.city || "",
        country: data.extracted.country || "",
        source_du_contact: "Carte de visite (OCR)",
        share_scope: "private",
      });
      setStep("review");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Extraction impossible.");
    } finally {
      setScanning(false);
    }
  };

  const saveTarget = async () => {
    if (!form.display_name?.trim()) {
      toast.error("Le nom est requis.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      const { data } = await api.post("/cdl/targets", payload);
      toast.success("Cible créée à partir de la carte.");
      navigate(`/app/commercial/list?highlight=${data.target_id || ""}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Création impossible.");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setStep("capture"); setPreview(null); setExtracted(null); setForm(null);
  };

  return (
    <IntelligenceHub activeTab="ocr">
      <div className="max-w-4xl mx-auto" data-testid="intel-ocr-page">
        {step === "capture" && (
          <CaptureStep
            scanning={scanning}
            preview={preview}
            fileRef={fileRef}
            cameraRef={cameraRef}
            onFile={handleFile}
          />
        )}
        {step === "review" && form && (
          <ReviewStep
            preview={preview}
            extracted={extracted}
            form={form}
            setForm={setForm}
            onBack={reset}
            onSave={saveTarget}
            saving={saving}
          />
        )}
      </div>
    </IntelligenceHub>
  );
}


function CaptureStep({ scanning, preview, fileRef, cameraRef, onFile }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-[#C5A880]/60 bg-white/70 p-10 text-center"
         data-testid="intel-ocr-capture">
      <div className="mx-auto h-14 w-14 rounded-full bg-[#FBF6EA] flex items-center justify-center">
        {scanning
          ? <Loader2 className="h-7 w-7 text-[#7A5B14] animate-spin" strokeWidth={1.5} />
          : <ScanLine className="h-7 w-7 text-[#7A5B14]" strokeWidth={1.5} />}
      </div>
      <h3 className="mt-5 font-display text-2xl text-[#1F2937] tracking-tight">
        Scanner une carte de visite
      </h3>
      <p className="mt-2 text-sm text-[#5B5B62] max-w-lg mx-auto leading-relaxed">
        Prenez ou téléversez une photo de carte de visite. Claude Sonnet 4.5 Vision extraira
        automatiquement les infos de contact — vous validerez avant l&apos;enregistrement.
        <br />
        <span className="text-xs opacity-70">L&apos;image n&apos;est jamais stockée.</span>
      </p>

      {preview?.url && (
        <div className="mt-6 flex justify-center">
          <img src={preview.url} alt="carte" className="max-h-56 rounded-lg border border-[#E4E1D8] shadow-sm" />
        </div>
      )}

      <input
        ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
        className="hidden" data-testid="intel-ocr-file-input"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
      <input
        ref={cameraRef} type="file" accept="image/*" capture="environment"
        className="hidden" data-testid="intel-ocr-camera-input"
        onChange={(e) => onFile(e.target.files?.[0])}
      />

      <div className="mt-6 flex flex-wrap gap-3 justify-center">
        <button
          onClick={() => cameraRef.current?.click()}
          disabled={scanning}
          data-testid="intel-ocr-camera-button"
          className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white px-5 py-2.5 text-sm font-medium transition-colors"
        >
          <Camera className="h-3.5 w-3.5" strokeWidth={2} />
          Prendre une photo
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={scanning}
          data-testid="intel-ocr-upload-button"
          className="inline-flex items-center gap-2 rounded-md border border-[#E4E1D8] bg-white hover:border-[#0B2545] hover:text-[#0B2545] disabled:opacity-60 text-[#1F2937] px-5 py-2.5 text-sm font-medium transition-colors"
        >
          <Upload className="h-3.5 w-3.5" strokeWidth={2} />
          Téléverser une image
        </button>
      </div>

      {scanning && (
        <div className="mt-6 text-sm text-[#7A5B14] flex items-center justify-center gap-2" data-testid="intel-ocr-scanning">
          <Loader2 className="h-4 w-4 animate-spin" /> Extraction en cours (Claude Vision)…
        </div>
      )}
    </div>
  );
}


const FIELDS = [
  { key: "display_name",      label: "Nom affiché *",     required: true },
  { key: "first_name",        label: "Prénom" },
  { key: "last_name",         label: "Nom" },
  { key: "organization_name", label: "Organisation" },
  { key: "role_or_title",     label: "Titre / poste" },
  { key: "email",             label: "Courriel",         type: "email" },
  { key: "phone",             label: "Téléphone",        type: "tel" },
  { key: "website",           label: "Site web" },
  { key: "linkedin_url",      label: "LinkedIn" },
  { key: "address",           label: "Adresse" },
  { key: "city",              label: "Ville" },
  { key: "country",           label: "Pays (ISO-2)",     max: 2 },
];

function ReviewStep({ preview, extracted, form, setForm, onBack, onSave, saving }) {
  const conf = Number(extracted?.confidence || 0);
  const confColor = conf >= 70 ? "text-emerald-700 bg-emerald-50 border-emerald-300"
                   : conf >= 40 ? "text-amber-800 bg-amber-50 border-amber-300"
                   : "text-red-800 bg-red-50 border-red-300";
  return (
    <div className="space-y-6" data-testid="intel-ocr-review">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">Étape 2 / 2</div>
          <h3 className="mt-1 font-display text-2xl text-[#1F2937] tracking-tight">
            Vérifier et enregistrer
          </h3>
          <p className="mt-1 text-sm text-[#5B5B62]">
            Modifiez les champs avant enregistrement — l&apos;IA peut se tromper.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-[#5B5B62] hover:text-[#0B2545]">
            <ArrowLeft className="h-3.5 w-3.5" /> Recommencer
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Preview + confidence */}
        <div>
          {preview?.url && (
            <img src={preview.url} alt="carte" className="w-full rounded-lg border border-[#E4E1D8] shadow-sm mb-3" />
          )}
          <div className={`rounded-md border px-3 py-2 text-xs ${confColor}`} data-testid="intel-ocr-confidence">
            <div className="font-mono uppercase tracking-widest opacity-70">Confiance IA</div>
            <div className="font-display text-2xl tabular-nums mt-0.5">{conf}%</div>
          </div>
          {extracted?.model && (
            <div className="mt-2 text-[10px] font-mono text-[#8B8B8F] break-all">
              {extracted.model}
            </div>
          )}
        </div>

        {/* Form */}
        <div className="rounded-lg border border-[#E4E1D8] bg-white p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mb-1">
                  {f.label}
                </label>
                <input
                  type={f.type || "text"}
                  maxLength={f.max || 400}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  data-testid={`intel-ocr-field-${f.key}`}
                  className="w-full rounded-md border border-[#E4E1D8] bg-white px-3 py-2 text-sm focus:border-[#0B2545] outline-none"
                />
              </div>
            ))}
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mb-1">
                Type
              </label>
              <select
                value={form.target_type}
                onChange={(e) => setForm((s) => ({ ...s, target_type: e.target.value }))}
                className="w-full rounded-md border border-[#E4E1D8] bg-white px-3 py-2 text-sm"
              >
                <option value="prospect">Prospect</option>
                <option value="partner">Partenaire</option>
                <option value="accountant">Comptable</option>
                <option value="real_estate_broker">Courtier immobilier</option>
                <option value="lawyer">Avocat</option>
                <option value="notary">Notaire</option>
                <option value="recruiter">Recruteur</option>
                <option value="organization">Organisation</option>
                <option value="employer">Employeur</option>
                <option value="event">Événement</option>
                <option value="other">Autre</option>
              </select>
            </div>
          </div>

          {extracted?.raw_text && (
            <details className="mt-4 text-xs text-[#5B5B62]">
              <summary className="cursor-pointer hover:text-[#0B2545]">Texte brut extrait de la carte</summary>
              <pre className="mt-2 p-3 rounded bg-[#F8F7F3] border border-[#E4E1D8] whitespace-pre-wrap text-[11px]">{extracted.raw_text}</pre>
            </details>
          )}

          <div className="mt-5 flex justify-end">
            <button
              onClick={onSave}
              disabled={saving}
              data-testid="intel-ocr-save"
              className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white px-5 py-2.5 text-sm font-medium transition-colors"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              {saving ? "Enregistrement…" : "Créer la cible"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
